import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const citiBankCaseStudy: ContentObject = {
    id: 'citi-bank-genai-dev',
    kind: 'system',
    type: 'case-study',
    name: 'GenAI Modernizes Legacy Systems & Elevates Developer Productivity at Citi',
    description:
        'Citi uses GitHub Copilot Enterprise and RAG on its internal code repository to boost developer productivity, modernize legacy systems, and streamline compliance, achieving significant efficiency gains.',
    url: 'https://americanbanker.com/news/why-citi-is-rolling-out-generative-ai-to-all-its-developers',
    scores: createScores({
        technicalImplementationDetails: 7.0,
        reliabilityOfSource: 8.0,
        relevanceToUseCase: 7.0,
        noVendorBias: 8.0,
        detailedMetrics: 7.0,
        strategicAndTacticalInformation: 8.0,
        relevanceToClient: 10.0,
    }),
    publishedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
    markdown: `# 1. Who the Source of Information Is
 
 Source Kind: Webpage (American Banker) 
 Key Source: Shadman Zafar, co-CIO for Citi's personal banking and wealth management and lead for the bank's generative AI work.
 
 Relevant Quote: 
 > ""I do believe it's a technology that will, in a sustainable way, have a long-term impact on how we do work for a couple of decades to come,"" said Shadman Zafar.
 
 # 2. Successes
 
 Citi's generative AI initiative has achieved notable milestones in streamlining developer productivity and modernizing internal systems. They began with a pilot program involving 250 developers that expanded to 700 and then to 2,000, signaling rapid adoption. Currently, between 500 and 1,000 developers are using GitHub Copilot on a daily basis.
 
 Key successes include: 
 - Rolling out GitHub Copilot to all of Citi's developers (around 40,000 employees) by mid-April. 
 - Accelerating the generation of reusable code from their own secure repository—thus reducing coding time and improving code quality. 
 - Modernizing legacy systems by using generative AI to translate older code, such as translating legacy code into Java, a much-needed initiative given the difficulty in finding experts in outdated languages. 
 - Initiating first drafts of compliance assessments whereby generative AI covers the initial hours of prep work in regulatory reviews, allowing compliance staff to focus on deeper analysis.
 
 Relevant Quote: 
 > ""Helping programmers become more productive, modernizing legacy systems and conducting compliance assessments are among the hundreds of gen AI use cases in production or in the pipeline,"" explained Zafar.
 
 # 3. Challenges
 
 Citi's approach to integrating generative AI into its developer workflows is not without challenges. A primary concern is the risk of plagiarism; generative AI might suggest code segments that are the intellectual property of external sources. This risk has fueled active discussion within their developer community. Additionally, strict compliance rules restrict developers from using these tools off-site, which limits the extent of shortcuts compared to non-enterprise environments.
 
 Key challenges include: 
 - Managing the fear of intellectual property infringement, where the AI may generate code from external sources that could potentially involve plagiarism. 
 - Ensuring strict compliance controls so that work remains within the secure boundaries of the bank's systems – a constraint that means the tool sometimes provides fewer lines of code compared to less-controlled environments.
 
 Relevant Quote: 
 > ""This has been a very active conversation in our developer committee,"" Zafar noted, adding, ""That's the difference between being a college student doing your homework and being a significant bank – you have to have a certain level of control.""
 
 # 4. Technical Details
 
 To address both productivity and risk, Citi has implemented several technical measures that harness the power of generative AI in a secure and controlled manner. At the heart of their initiative is GitHub Copilot Enterprise, chosen specifically for its capability to enforce robust controls on both the inputs and outputs of the AI system. This ensures that generated code adheres to Citi's stringent standards.
 
 Additional technical components include: 
 - Deployment of retrieval augmented generation (RAG). This approach retrieves relevant data and documents from Citi's own approved code repository—millions of lines of proven code—to serve as context for the large language model, thereby reducing the risk of misinformation or hallucination.  
 - Use of an internal repository that is far smaller than the entire internet yet contains rigorously tested and approved code, which helps maintain compliance and quality. 
 - Ongoing training and strict vetting processes where every generative AI idea passes through technological and business assessments, ensuring transparency, explainability, and alignment with regulatory requirements.
 
 Relevant Quote: 
 > ""When it pulls larger segments of code, it picks out of the approved Citi code repository, which is obviously not as big as the internet, but it still has millions and millions of lines of code,"" explained Zafar.
 
 # 5. Results
 
 The results of Citi's generative AI efforts are multifold and impactful: 
 - Developers experience a boost in productivity as repetitive tasks and code synthesis are significantly accelerated. 
 - Legacy system modernization becomes more feasible on a large scale – with AI translating old code to modern languages like Java, saving considerable time and resources. 
 - In regulatory compliance, initial prep work (roughly the first four hours) is streamlined, enabling compliance experts to concentrate on detailed assessment rather than routine summarization of regulations. 
 - The controlled use of AI assures that the code generated is tethered to verified, internal benchmarks, thus reducing risk.
 
 Relevant Quote: 
 > ""It handles the first four hours of prep needed for a compliance review,"" stated Zafar, highlighting both efficiency gains and improved focus for human oversight.
 
 # 6. Vendors Used
 
 Citi's primary vendor for their generative AI-powered coding assistance is GitHub through its Copilot Enterprise offering. This vendor is pivotal to their efforts as it permits enhanced control over code inputs and outputs, making it a secure solution for enterprise-scale application in a heavily regulated environment.
 
 Relevant Quote: 
 > ""Zafar chose GitHub's Copilot Enterprise because it allows for better control of inputs and outputs,"" he said, underlining the strategic decision behind vendor selection.
 
 # Conclusion
 
 This case study illustrates how Citi is leveraging generative AI to enhance the efficiency and productivity of its software developers, simultaneously modernizing legacy systems and streamlining compliance processes—all while managing risks associated with AI-generated code through carefully chosen technical solutions and vendor partnerships.`,
    references: [
        {
            id: '1',
            name: 'Why Citi is rolling out generative AI to all its developers',
            author: 'Penny Crosman',
            year: '2024', // Assuming 2024 based on context
            publisher: 'American Banker',
            url: 'https://americanbanker.com/news/why-citi-is-rolling-out-generative-ai-to-all-its-developers',
        },
    ],
};
